import { useState } from 'react'
import { navigate } from 'gatsby'

import Icon from '../ui/Icon'
import { ArrowRight } from '../Icons/ArrowRight'
import './styles.css'
import { ArrowLeftStarterKitIcon } from '../Icons/ArrowLeftStarterKitIcon'

type StepOption = {
  label: string
  title: string
  subtitle?: string
}

type StepData = {
  title: string
  options: StepOption[]
}

interface StarterKitFormProps {
  onClose: () => void
}

const stepsData: StepData[] = [
  {
    title: 'Para quem é esse kit?',
    options: [
      {
        label: 'Homem',
        title: 'Homem',
      },
      {
        label: 'Mulher',
        title: 'Mulher',
      },
    ],
  },
  {
    title: 'Qual seu nível de experiência em trilhas e acampamento?',
    options: [
      {
        label: 'Iniciante',
        title: 'Iniciante',
        subtitle: 'Nunca acampei antes ou faço trilhas leves e curtas',
      },
      {
        label: 'Intermediário',
        title: 'Intermediário',
        subtitle: 'Já acampei algumas vezes ou faço trilhas moderadas',
      },
      {
        label: 'Experiente',
        title: 'Experiente',
        subtitle: 'Acampo com frequência e faço trilhas longas e exigêntes',
      },
    ],
  },
]

const LANDING_PAGE_MAP: Record<string, string> = {
  Homem_Iniciante: '/campanha/pack-iniciante-masculino',
  Homem_Intermediário: '/campanha/pack-intermediario-masculino',
  Homem_Experiente: '/campanha/pack-avancado-masculino',
  Mulher_Iniciante: '/campanha/pack-iniciante-feminino',
  Mulher_Intermediário: '/campanha/pack-intermediario-feminino',
  Mulher_Experiente: '/campanha/pack-avancado-feminino',
}

const getURL = (gender: string, level: string) => {
  const key = `${gender}_${level}`

  return LANDING_PAGE_MAP[key] || '/'
}

export function StarterKitForm({ onClose }: StarterKitFormProps) {
  const [formStep, setFormStep] = useState(0)
  const [values, setValues] = useState<{ [key: number]: string }>({})

  const handleOptionChange = (stepIdx: number, value: string) => {
    setValues((prev) => ({ ...prev, [stepIdx]: value }))
  }

  const handleNextStep = () => {
    setFormStep((prevStep) => prevStep + 1)
  }

  const handlePreviousStep = () => {
    setFormStep((prevStep) => prevStep - 1)
  }

  const handleFinishClick = async () => {
    const { 0: gender, 1: level } = values

    const lpUrl = getURL(gender, level)

    onClose()

    if (lpUrl) {
      navigate(lpUrl)
    }
  }

  return (
    <div className="restructure-small-desktop:min-w-[480px]">
      <header className="w-full flex items-center justify-end py-4 px-4 border-b border-borderPrimary restructure-maxTablet:hidden">
        <button className="h-[48px]" aria-label="close modal" onClick={onClose}>
          <Icon name="FreightClose" width={24} height={24} />
        </button>
      </header>
      <div
        className={`px-[18px] py-[29px] progress-bar grid grid-cols-${stepsData.length} gap-2 restructure-maxTablet:border-t restructure-maxTablet:border-borderPrimary`}
      >
        {stepsData.map((_, stepIdx) => (
          <div
            key={stepIdx}
            className={`h-1  ${
              stepIdx === formStep
                ? 'bg-restructure-background-information-3'
                : 'bg-[#D0D0D0]'
            }`}
          />
        ))}
        <div className="col-span-full">
          {stepsData.map((step, stepIdx) => {
            if (stepIdx !== formStep) {
              return null
            }

            return (
              <div key={stepIdx} className="mt-4 starterkit-fade">
                <p className="text-font-size-4 font-medium text-restructure-primary">
                  0{stepIdx + 1}
                </p>
                <fieldset>
                  <legend className="text-font-size-7 leading-[29px] font-semibold text-restructure-primary">
                    {step.title}
                  </legend>
                  <div className="mt-6 flex flex-col gap-3">
                    {step.options.map((option, optionIdx) => (
                      <label
                        key={optionIdx}
                        className="option flex items-center gap-4 h-[76px] px-6 border border-gray cursor-pointer"
                      >
                        <input
                          type="radio"
                          name={step.title}
                          value={option.label}
                          className="starter-kit-input--radio"
                          checked={values[stepIdx] === option.label}
                          onChange={() =>
                            handleOptionChange(stepIdx, option.label)
                          }
                        />
                        <div className="flex flex-col gap-0.5">
                          <span className="text-font-size-4 leading-[21px] font-semibold text-restructure-primary">
                            {option.title}
                          </span>
                          {option.subtitle && (
                            <span className="text-font-size-4 leading-[21px] font-medium text-restructure-primary">
                              {option.subtitle}
                            </span>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                </fieldset>
              </div>
            )
          })}
        </div>
      </div>
      <footer className="px-4 py-4 mt-4 flex gap-2">
        {formStep > 0 && (
          <button
            className="w-[91px] h-16 px-4 py-1 grid place-items-center border border-lightGray"
            onClick={handlePreviousStep}
            aria-label="Voltar"
          >
            <ArrowLeftStarterKitIcon />
          </button>
        )}
        {formStep < stepsData.length - 1 && (
          <button
            className="h-16 flex justify-center items-center gap-2 bg-restructure-background-primary-inverted text-restructure-primary-inverted py-1 px-4 flex-1"
            onClick={handleNextStep}
            disabled={!values[formStep]}
            style={{
              opacity: !values[formStep] ? 0.5 : 1,
              cursor: !values[formStep] ? 'not-allowed' : 'pointer',
            }}
          >
            Próximo <ArrowRight color="#fff" />
          </button>
        )}
        {formStep === stepsData.length - 1 && (
          <button
            className="h-16 flex justify-center items-center gap-2 bg-restructure-border-action text-restructure-primary-inverted py-1 px-4 flex-1"
            disabled={!values[formStep]}
            onClick={handleFinishClick}
            style={{
              opacity: !values[formStep] ? 0.5 : 1,
              cursor: !values[formStep] ? 'not-allowed' : 'pointer',
            }}
          >
            Ver resultados <ArrowRight color="#fff" />
          </button>
        )}
      </footer>
    </div>
  )
}
